/**
 * Unterseiten „Kündigungsquoten" und „Cashflow" der Sektion: die bestehenden Seiten unverändert eingebettet (Till
 * 08.10.2026, Regel 10 bis P5), mit dem Rahmen der Sektion (P2-V3 § 2.10/§ 2.11: Rahmen als Stichpunkte, Wörter, die hier
 * anders heißen, und — F1 — ein Satz direkt UNTER den zugeklappten Streitfällen). Dieselben Endpunkte unter
 * /api/plx-orders (Gruppe fit_products, kein 403 für Sektionsnutzer). Begriffe und Spalten der Alt-Komponenten
 * werden mit der Ablösung der alten Seiten (P5) an die Sektion angeglichen.
 */
import { FC, ReactNode } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import ChapterPage from '../../../components/auswertungen/ChapterPage';
import KuendigungsquotenPage from '../../plx-orders/kuendigungsquoten';
import PlxCashflowForecastPage from '../../plx-orders/cashflow-forecast';
// --- P5 --- Umsatz-Diskrepanzen und Anfo-Bewertung (bisher /plx-orders/revenue-variance, /anfo-valuation), eingebettet.
import PlxRevenueVariancePage from '../../plx-orders/revenue-variance';
import PlxAnfoValuationPage from '../../plx-orders/anfo-valuation';
import FilterBar from '../../../components/FilterBar';

const ZURUECK = { to: '/auswertungen/umsatz', label: 'Umsatz & Bezahlqualität' };
const NUR_PLX = [{ key: 'plx_orders', label: 'Bestellsystem (PL-X)' }];

const Rahmen: FC<{ punkte: ReactNode[] }> = ({ punkte }) => (
  <Alert severity="info" icon={false} sx={{ mb: 2, maxWidth: '80ch' }}>
    <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5, lineHeight: 1.5 } }}>
      {punkte.map((p, i) => (
        <li key={i}>{p}</li>
      ))}
    </Box>
  </Alert>
);

export const KuendigungUnterseite: FC = () => (
  <ChapterPage
    title="Kündigungsquoten"
    question="Wie viele Quartalsabos zahlen die erste und die zweite Rechnung nicht — je Bestellmonat?"
    zurueck={ZURUECK}
    standQuellen={NUR_PLX}
  >
    <Rahmen
      punkte={[
        // C10 (Persona-Umbau 28.10.2026): der Satz zuerst, damit niemand „Kündigung“ als echte Kündigung liest.
        <>
          <strong>Kündigungsquote heißt hier: Rechnung nicht bezahlt</strong> (das Gegenstück zur Bezahlquote des Kapitels).
        </>,
        <>
          Genauer: Anteil der Quartalsabos, die die 1. (Kü1) bzw. 2. Rechnung (Kü2) nicht bezahlen, auch
          ohne echte Kündigung. <strong>Hoch ist schlecht.</strong> Die Farbe der großen Zahl kennzeichnet nur die Reihe, sie bewertet nicht.
        </>,
        <>
          <strong>Zwei Rechenarten (Umschalter oben):</strong> „Laufzeit“ (Standard über den Link aus dem Kapitel) zählt, wer gezahlt hat und
          geblieben ist. „Betrag“ zählt nur voll bezahlte Rechnungen; ältere Monate ohne damaligen Preis bleiben leer. Beide messen etwas anderes als
          die Bezahlquote des Kapitels (dort zählt jede Zahlung über 1 €).
        </>,
        <>
          Alle Beträge brutto. Wörter dieser Seite: Kü1/Kü2 = 1./2. Rechnung nicht bezahlt · Rolling 3M = gleitender Schnitt über drei
          Bestellmonate · „unreif“ = die Rechnung war noch nicht fällig · Status B/L = beendet / läuft.
        </>,
      ]}
    />
    <KuendigungsquotenPage eingebettet />
    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, mb: 3, maxWidth: '75ch', lineHeight: 1.6 }}>
      ▲ Direkt darüber, zugeklappt: <strong>Streitfälle</strong> — einzelne Bestellungen, bei denen die beiden Rechenarten sich widersprechen. Eine
      Prüfliste für Controlling; die Quoten oben brauchen sie nicht. Zum Öffnen auf die Leiste klicken.
    </Typography>
  </ChapterPage>
);

export const CashflowUnterseite: FC = () => (
  <ChapterPage
    title="Cashflow"
    question="Welche Zahlungen kommen in den nächsten 13 Wochen, und wie lief das Quartal gegen den Plan?"
    zurueck={ZURUECK}
    standQuellen={NUR_PLX}
  >
    <Rahmen
      punkte={[
        // C10 (Persona-Umbau 28.10.2026): „ANFO“ als eigener erster Punkt, nicht in der Wörterliste versteckt.
        <>
          <strong>„ANFO“ heißt auf dieser Seite: ein Abo in der Testphase</strong> — nicht die Anfo des übrigen Bereichs (dort jede
          Bestellung ohne Storno und Testbestellung).
        </>,
        <>
          Alle Beträge <strong>brutto</strong> (inkl. MwSt.), wie auf dem Konto. Die „nächsten Rechnungen“ im Kapitel kommen aus{' '}
          <strong>derselben Berechnung</strong>, dort netto.
        </>,
        <>
          <strong>Drei Wörter heißen hier anders als im Rest des Bereichs:</strong> „Ist netto“ = nach Rückbuchungen, weiter <em>inkl.</em> MwSt. ·
          „bereinigt“ = nach erwarteten Ausfällen, „Plan brutto“ = der Plan, wenn alle zahlen · „ANFO“ = Abo in der Testphase (siehe oben).
        </>,
        <>Weitere Wörter: LBA-Woche = bis zu welcher Kalenderwoche das Abo bezahlt ist · Mahnstufe 0 = keine offene Mahnung.</>,
      ]}
    />
    <PlxCashflowForecastPage eingebettet />
  </ChapterPage>
);


// --- P5 --- (Plan 2026-10-27-p4-hebel-p5-kern Paket B): zwei weitere Alt-Seiten unverändert eingebettet. Beide lesen den
// Zeitraum aus dem globalen Filter (FilterContext) — die Filterleiste steht deshalb mit auf der Unterseite und schreibt
// startDate/endDate in die URL (Lesezeichen und Umleitung aus /plx-orders/… behalten den Zeitraum).
const Zeitraum: FC = () => (
  <Box sx={{ mb: 2, '& > *': { m: '0 !important' } }}>
    <FilterBar filterConfig={['startDate', 'endDate']} syncWithUrl />
  </Box>
);

export const DiskrepanzenUnterseite: FC = () => (
  <ChapterPage
    title="Umsatz-Diskrepanzen"
    question="Wo weicht der tatsächlich gezahlte Betrag einer Bestellung vom Betrag ab, den Preis und Laufzeit erwarten lassen?"
    zurueck={ZURUECK}
    standQuellen={NUR_PLX}
  >
    <Rahmen
      punkte={[
        <>
          Prüfseite für Controlling: je bezahlter Bestellung (Quartals- und Jahresabos) der <strong>erwartete Betrag</strong> aus Preis und
          Laufzeit gegen das <strong>tatsächlich Gezahlte</strong>. Negativ = weniger eingegangen als erwartet (Rückbuchung, Storno).
        </>,
        <>
          Alle Beträge <strong>brutto</strong> (inkl. MwSt.), anders als im übrigen Bereich. Testbestellungen und Bestellungen mit einer
          Zahlung, die in den letzten 14 Tagen fehlschlug, sind ausgenommen.
        </>,
        <>Zeitraum = Bestelltag. Ein Klick auf eine Bestellung öffnet ihr Detail.</>,
      ]}
    />
    <Zeitraum />
    <PlxRevenueVariancePage eingebettet />
  </ChapterPage>
);

export const AnfoBewertungUnterseite: FC = () => (
  <ChapterPage
    title="Anfo-Bewertung"
    question="Wie verhält sich der Anfo-Wert je Produkt zu dem, was die Anfos bisher bezahlt haben?"
    zurueck={ZURUECK}
    standQuellen={[...NUR_PLX, { key: 'anfo_werte', label: 'Anfo-Werte' }]}
  >
    <Rahmen
      punkte={[
        <>
          Je Produkt: Anfos im Zeitraum, <strong>Summe der Anfo-Werte</strong> (Erwartung über 36 Monate) und der bisher{' '}
          <strong>bezahlte Umsatz</strong>. Wie der Anfo-Wert entsteht, zeigt die Unterseite „Wie der Anfo-Wert entsteht“.
        </>,
        <>
          Alle Beträge <strong>brutto</strong> (inkl. MwSt.), anders als im übrigen Bereich (dort netto). Die Bewertung ist die heutige
          des Produkts — junge Zeiträume haben naturgemäß wenig bezahlt.
        </>,
        // P5-Review #14: zwei Zahlen heißen hier wie im Kapitel, rechnen aber anders (Rechnung der alten Seite bleibt).
        <>
          <strong>Zwei Zahlen weichen vom Kapitel ab:</strong> „Conv. %“ zählt alle Anfos des Zeitraums, auch vorläufige und
          junge, die noch nicht zahlen konnten — die Bezahlquote im Kapitel nur Anfos ab 60 Tagen, deshalb liegt sie höher.
          Der „Anfo-Wert“ ist der Wert jeder Anfo im Modell (bis zur Zahlung der Wert ihres Angebots, danach aus ihrer eigenen
          Zahlung hochgerechnet, brutto), nicht der „Erwartete Wert bei Bestellung“ des Kapitels (netto, immer der heutige
          Angebotswert).
        </>,
      ]}
    />
    <Zeitraum />
    <PlxAnfoValuationPage eingebettet />
  </ChapterPage>
);
